import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, ArrowRight, Check, Info } from "lucide-react";
import Header from "@/components/landing/Header";
import Footer from "@/components/landing/Footer";
import Seo from "@/components/Seo";
import { getVisa, VISAS } from "@/data/visas";

function BlockContent({ block }) {
  return (
    <>
      {block.paragraphs?.map((p, j) => (
        <p key={j} className="mt-5 text-base leading-relaxed text-ink-muted">
          {p}
        </p>
      ))}
      {block.list && (
        <ul className="mt-6 space-y-3">
          {block.list.map((item, j) => (
            <li
              key={j}
              className="flex items-start gap-3 text-base leading-relaxed text-ink-muted"
            >
              <Check className="mt-1 h-4 w-4 shrink-0 text-clay" />
              {item}
            </li>
          ))}
        </ul>
      )}
    </>
  );
}

export default function VisaPage() {
  const { slug } = useParams();
  const visa = getVisa(slug);

  if (!visa) {
    return (
      <>
        <Header />
        <main className="bg-sand pb-32 pt-44">
          <div className="mx-auto max-w-3xl px-6 text-center">
            <h1
              className="font-serif text-4xl tracking-tight text-ink"
              data-testid="visa-not-found"
            >
              Seite nicht gefunden
            </h1>
            <Link
              to="/visa"
              data-testid="visa-back-link"
              className="mt-8 inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-clay"
            >
              <ArrowLeft className="h-4 w-4" /> Zur Übersicht
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const others = VISAS.filter((v) => v.slug !== slug);

  return (
    <>
      <Seo
        title={visa.title}
        description={visa.description}
        keywords={visa.keywords}
        jsonLd={[
          {
            "@type": "Service",
            name: visa.title,
            description: visa.description,
            inLanguage: "de",
            provider: {
              "@type": "ProfessionalService",
              name: "Florida Business & Relocation",
            },
            areaServed: { "@type": "State", name: "Florida" },
          },
          {
            "@type": "FAQPage",
            mainEntity: visa.faq.map((f) => ({
              "@type": "Question",
              name: f.q,
              acceptedAnswer: { "@type": "Answer", text: f.a },
            })),
          },
          {
            "@type": "BreadcrumbList",
            itemListElement: [
              { "@type": "ListItem", position: 1, name: "Startseite", item: "https://auswandernflorida.com/" },
              { "@type": "ListItem", position: 2, name: "Visa & Green Card", item: "https://auswandernflorida.com/visa" },
              { "@type": "ListItem", position: 3, name: visa.title },
            ],
          },
        ]}
      />
      <Header />
      <main>
        <article data-testid="visa-page" className="bg-sand pb-24 pt-36 lg:pt-44">
          <div className="mx-auto max-w-3xl px-6">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              <Link
                to="/visa"
                data-testid="visa-back-link"
                className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-ink-muted transition-colors hover:text-clay"
              >
                <ArrowLeft className="h-4 w-4" /> Visa &amp; Green Card
              </Link>
            </motion.div>

            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.05 }}
              className="mt-10 text-xs uppercase tracking-[0.3em] text-clay"
              data-testid="visa-badge"
            >
              {visa.badge}
            </motion.p>

            <motion.h1
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.1 }}
              className="mt-6 font-serif text-4xl leading-tight tracking-tight text-ink sm:text-5xl"
              data-testid="visa-title"
            >
              {visa.title}
            </motion.h1>

            <motion.p
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.2 }}
              className="mt-8 border-l-2 border-clay pl-6 font-serif text-xl italic leading-relaxed text-ink-muted"
              data-testid="visa-lead"
            >
              {visa.lead}
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.3 }}
              className="mt-10"
            >
              <a
                href="/kontakt"
                data-testid="visa-cta-top"
                className="group inline-flex items-center gap-3 bg-clay px-8 py-4 text-xs uppercase tracking-[0.2em] text-sand transition-colors hover:bg-clay-dark"
              >
                Chancen kostenlos prüfen lassen
                <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
              </a>
            </motion.div>

            <div className="mt-16 space-y-16" data-testid="visa-body">
              {visa.blocks.map((block, i) => (
                <motion.section
                  key={block.heading}
                  initial={{ opacity: 0, y: 24 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-60px" }}
                  transition={{ duration: 0.6 }}
                  data-testid={`visa-section-${i}`}
                >
                  <h2 className="font-serif text-2xl tracking-tight text-ink sm:text-3xl">
                    {block.heading}
                  </h2>
                  <BlockContent block={block} />
                  {block.sub?.map((s, k) => (
                    <div key={k} className="mt-10">
                      <h3 className="font-serif text-xl tracking-tight text-ink sm:text-2xl">
                        {s.heading}
                      </h3>
                      <BlockContent block={s} />
                    </div>
                  ))}
                </motion.section>
              ))}
            </div>

            <motion.section
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.6 }}
              className="mt-20"
              data-testid="visa-faq"
            >
              <h2 className="font-serif text-2xl tracking-tight text-ink sm:text-3xl">
                {visa.faqTitle}
              </h2>
              <div className="mt-8 space-y-10">
                {visa.faq.map((f, i) => (
                  <div key={i} data-testid={`visa-faq-item-${i}`}>
                    <h3 className="font-serif text-lg tracking-tight text-ink sm:text-xl">
                      {f.q}
                    </h3>
                    <p className="mt-3 text-sm leading-relaxed text-ink-muted md:text-base">
                      {f.a}
                    </p>
                  </div>
                ))}
              </div>
            </motion.section>

            <motion.div
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.6 }}
              className="mt-16 border-l-2 border-clay bg-sand-deep p-8 sm:p-10"
              data-testid="visa-myth-box"
            >
              <p className="flex items-center gap-3 font-serif text-xl italic tracking-tight text-ink sm:text-2xl">
                <Info className="h-5 w-5 shrink-0 text-clay" />
                {visa.myth.title}
              </p>
              <p className="mt-4 text-sm leading-relaxed text-ink-muted md:text-base">
                {visa.myth.text}
              </p>
            </motion.div>

            <div
              className="mt-16 border border-line bg-sand-deep p-10"
              data-testid="visa-cta-box"
            >
              <p className="font-serif text-2xl italic leading-snug tracking-tight text-ink">
                Wie wir Sie begleiten
              </p>
              <p className="mt-4 text-sm leading-relaxed text-ink-muted md:text-base">
                {visa.closing}
              </p>
              <a
                href="/kontakt"
                data-testid="visa-cta-button"
                className="group mt-8 inline-flex items-center gap-3 bg-clay px-8 py-4 text-xs uppercase tracking-[0.2em] text-sand transition-colors hover:bg-clay-dark"
              >
                Kostenloses Erstgespräch buchen
                <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
              </a>
              <p className="mt-6 text-xs leading-relaxed text-ink-muted/70">
                Komplettservice von A bis Z: Wir betreuen Ihr gesamtes Vorhaben
                persönlich und auf Deutsch – von der ersten Strategie bis zur
                fertigen Antragstellung.
              </p>
            </div>

            <div className="mt-24 border-t border-line pt-12">
              <p className="text-xs uppercase tracking-[0.3em] text-clay">
                Weitere Wege in die USA
              </p>
              <ul className="mt-8 space-y-6" data-testid="visa-more-list">
                {others.map((other) => (
                  <li key={other.slug}>
                    <Link
                      to={`/visa/${other.slug}`}
                      data-testid={`visa-more-${other.slug}`}
                      className="group flex items-baseline justify-between gap-6"
                    >
                      <span className="font-serif text-xl tracking-tight text-ink transition-colors group-hover:text-clay sm:text-2xl">
                        {other.title}
                      </span>
                      <ArrowRight className="h-4 w-4 shrink-0 text-clay transition-transform group-hover:translate-x-1" />
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </article>
      </main>
      <Footer />
    </>
  );
}
